import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import slides from "./imageUrl";
import "./style.css";

/**
 * Testimonials — Custom framer-motion carousel
 * Replaces AwesomeSlider with a gold-ring card carousel
 * All 5 testimonials from imageUrl.json are preserved
 */
const Testimonials = () => {
  const record    = slides.photoset.photo;
  const total     = record.length;
  const [active, setActive]   = useState(0);
  const [dir,    setDir]      = useState(1);
  const intervalRef           = useRef(null);

  const goto = (idx, direction = 1) => {
    setDir(direction);
    setActive(idx);
  };

  const next = () => goto((active + 1) % total,  1);
  const prev = () => goto((active - 1 + total) % total, -1);

  /* Auto-advance every 5s */
  useEffect(() => {
    intervalRef.current = setInterval(next, 5000);
    return () => clearInterval(intervalRef.current);
  }, [active]);

  const variants = {
    enter: (d) => ({ opacity: 0, x: d > 0 ? 60 : -60 }),
    center: { opacity: 1, x: 0, transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] } },
    exit:  (d) => ({ opacity: 0, x: d > 0 ? -60 : 60, transition: { duration: 0.35 } }),
  };

  const slide = record[active];

  return (
    <section className="testi-section udg-section" id="testimonials">
      {/* Header */}
      <div className="udg-section-header">
        <motion.span
          className="udg-eyebrow"
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          Voices from the Field
        </motion.span>
        <motion.h2
          className="udg-section-title font-display"
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.1, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        >
          Testimonials
        </motion.h2>
        <motion.span
          className="udg-gold-rule"
          style={{ margin: "0 auto", transformOrigin: "left" }}
          initial={{ opacity: 0, scaleX: 0 }}
          whileInView={{ opacity: 1, scaleX: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2, duration: 0.6 }}
        />
      </div>

      {/* Carousel */}
      <motion.div 
        className="testi-carousel"
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.2 }}
        transition={{ duration: 0.8, ease: "easeOut" }}
      >
        <AnimatePresence mode="wait" custom={dir}>
          <motion.div
            key={active}
            className="testi-card udg-card"
            custom={dir}
            variants={variants}
            initial="enter"
            animate="center"
            exit="exit"
          >
            {/* Photo */}
            <div className="testi-photo-ring">
              <img
                src={`https://live.staticflickr.com/${slide.server}/${slide.id}_${slide.secret}_m.jpg`}
                alt={slide.name}
                className="testi-photo"
              />
            </div>

            {/* Quote */}
            <blockquote className="testi-quote">
              "{slide.description}"
            </blockquote>

            {/* Name + college */}
            <div className="testi-meta">
              <span className="testi-name">{slide.name.trim()}</span>
              <span className="testi-college">{slide.college}</span>
            </div>
          </motion.div>
        </AnimatePresence>

        {/* Prev / Next */}
        <button
          className="testi-arrow testi-arrow--prev"
          onClick={() => { clearInterval(intervalRef.current); prev(); }}
          aria-label="Previous testimonial"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <path d="M15 18l-6-6 6-6" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
        <button
          className="testi-arrow testi-arrow--next"
          onClick={() => { clearInterval(intervalRef.current); next(); }}
          aria-label="Next testimonial"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <path d="M9 18l6-6-6-6" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
      </motion.div>

      {/* Dot nav */}
      <div className="testi-dots" role="tablist">
        {record.map((_, i) => (
          <button
            key={i}
            className={`testi-dot${i === active ? " testi-dot--active" : ""}`}
            onClick={() => { clearInterval(intervalRef.current); goto(i, i > active ? 1 : -1); }}
            aria-label={`Testimonial ${i + 1}`}
            role="tab"
            aria-selected={i === active}
          />
        ))}
      </div>
    </section>
  );
};

export default Testimonials;
